/**
 * WidgetRenderer — Renders a single widget by its registered id.
 *
 * Handles:
 *  - Lookup from the registry
 *  - Passing merged config (defaults + stored overrides) to the component
 *  - Error boundary per widget so one crash doesn't take down the whole layout
 *  - Loading skeleton while the widget module resolves
 */

import React, { Component, Suspense, useCallback } from 'react';
import { Loader, Icon } from 'monday-ui-react-core';
import { Alert } from 'monday-ui-react-core/icons';
import { widgetRegistry } from './WidgetRegistry';
import type { WidgetProps, WidgetDefinition, MondayContext } from '../core/types';

// -------------------------------------------------------
// Error boundary (class component — required by React API)
// -------------------------------------------------------

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class WidgetErrorBoundary extends Component<
  { widgetId: string; children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { widgetId: string; children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[WidgetRenderer] Widget "${this.props.widgetId}" threw:`, error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="widget-error-state">
          <Icon icon={Alert} iconSize={24} className="widget-error-icon" />
          <p className="widget-error-title">Widget failed to load</p>
          <p className="widget-error-detail">{this.state.error?.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// -------------------------------------------------------
// Loading skeleton
// -------------------------------------------------------

function WidgetSkeleton({ label }: { label?: string }) {
  return (
    <div className="widget-skeleton" aria-label={label ? `Loading ${label}` : 'Loading widget'}>
      <Loader size={32} />
    </div>
  );
}

// -------------------------------------------------------
// WidgetRenderer
// -------------------------------------------------------

export interface WidgetRendererProps {
  /** Registered widget id, e.g. "my_tasks" */
  widgetId: string;
  /** Unique per-grid-item identifier (from react-grid-layout) */
  instanceId: string;
  /** Stored config overrides for this instance */
  instanceConfig?: Record<string, unknown>;
  /** Whether the page builder is in edit mode */
  isEditMode: boolean;
  /** monday SDK context */
  context: MondayContext;
  /** Grid item pixel dimensions (provided by PageBuilder) */
  width?: number;
  height?: number;
  /** Callback to open the config panel for this widget */
  onOpenConfig: (instanceId: string) => void;
}

export function WidgetRenderer({
  widgetId,
  instanceId,
  instanceConfig = {},
  isEditMode,
  context,
  width,
  height,
  onOpenConfig,
}: WidgetRendererProps) {
  const definition: WidgetDefinition | undefined = widgetRegistry.getWidget(widgetId);

  const handleOpenConfig = useCallback(() => {
    onOpenConfig(instanceId);
  }, [onOpenConfig, instanceId]);

  if (!definition) {
    return (
      <div className="widget-unknown-state">
        <p>Unknown widget: <code>{widgetId}</code></p>
        {isEditMode && (
          <p className="widget-unknown-hint">
            This widget may have been removed. You can delete it from the layout.
          </p>
        )}
      </div>
    );
  }

  // Merge defaults with stored instance config
  const mergedConfig: Record<string, unknown> = {
    ...definition.defaultConfig,
    ...instanceConfig,
  };

  const widgetProps: WidgetProps = {
    instanceId,
    config: mergedConfig,
    onOpenConfig: handleOpenConfig,
    isEditMode,
    context,
    width,
    height,
  };

  const WidgetComponent = definition.component;

  return (
    <WidgetErrorBoundary widgetId={widgetId}>
      <Suspense fallback={<WidgetSkeleton label={definition.displayName} />}>
        <div
          className={`widget-container widget-category-${definition.category}`}
          data-widget-id={widgetId}
          data-instance-id={instanceId}
        >
          <WidgetComponent {...widgetProps} />
        </div>
      </Suspense>
    </WidgetErrorBoundary>
  );
}
